/* ==========================================================================
   Crumpaw · Personalised Reflective Dog Collar — buy box controller.

   One custom element, <cp-rc-buybox>, drives all three layouts of
   cp-rc-main (complete, quick, guided). The layouts differ only in which
   controls are on the page, so every ref lookup may come back null and every
   write is guarded.

   Degradation contract: if anything here throws, the form still sells a
   collar. Colour and size are real radios inside the form, there is a
   <noscript> variant select, and the name and phone inputs carry their own
   properties[...] names.

   Money is never invented. Every price is the real variant's cents put
   through the shop's money format; pack savings are a display setting whose
   matching automatic discount lives in admin.

   The plate is a claim about the finished collar, so it follows one rule:
   once a name is typed, the plate shows exactly the values the form will
   submit. Before that it shows the labelled example pair. Fields are
   sanitised in place (allow-list, cap applied after stripping) so the input,
   the plate and the line item property can never disagree.

   The form always posts ONE collar. Horizon posts FormData(form), so the
   extra collars of a pack and the optional lead go in as their own lines
   after the form's add lands, and the first collar comes back out if they
   fail — a cart holding one collar against a two-collar price is worse than
   an empty one.
   ========================================================================== */

(() => {
  'use strict';

  if (typeof window === 'undefined' || !window.customElements) return;
  if (customElements.get('cp-rc-buybox')) return;

  const CART_UPDATE = 'cart:update';
  const SOURCE = 'cp-rc';
  const PENDING_TTL = 20000;

  /* Letters, digits, space, apostrophe, hyphen and accented Latin. Everything
     else — emoji, zero-width and bidi controls included — is dropped, because
     a character the laser cannot cut must not reach the order. */
  const NAME_ALLOWED = /[^A-Za-z0-9 '\-À-ÖØ-öø-ſ]/g;
  const PHONE_ALLOWED = /[^0-9 +()\-]/g;

  const qs = (root, name) => root.querySelector(`[data-cp-rc="${name}"]`);
  const qsa = (root, name) => Array.from(root.querySelectorAll(`[data-cp-rc="${name}"]`));
  const setText = (node, value) => {
    if (node) node.textContent = value == null ? '' : String(value);
  };
  const show = (node, on) => {
    if (node) node.hidden = !on;
  };

  const formatMoney = (cents, format) => {
    const amount = Number(cents) || 0;
    const group = (value, precision, thousands, decimal) => {
      const parts = (value / 100).toFixed(precision).split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? `${whole}${decimal}${parts[1]}` : whole;
    };
    if (typeof format !== 'string' || format.indexOf('{{') === -1) {
      return `$${group(amount, 2, ',', '.')}`;
    }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_m, token) => {
      switch (token) {
        case 'amount_no_decimals':
          return group(amount, 0, ',', '.');
        case 'amount_with_comma_separator':
          return group(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(amount, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return group(amount, 2, "'", '.');
        default:
          return group(amount, 2, ',', '.');
      }
    });
  };

  /**
   * A pack's price in cents: the line total less the tier's percentage.
   *
   * Rounded the way Shopify rounds a percentage discount — once, on the
   * amount off — so the figure on the page is the figure the cart reaches.
   * A percentage is used rather than a flat saving because the collar is
   * three prices (S/M/L) and one dollar figure cannot be right for all of
   * them. This still only DISPLAYS the price: the automatic discount in
   * admin is what charges it.
   */
  const packTotal = (unit, quantity, percent) => {
    const count = Math.max(1, Number(quantity) || 1);
    const gross = (Number(unit) || 0) * count;
    const pct = Math.min(100, Math.max(0, Number(percent) || 0));
    return Math.max(0, gross - Math.round((gross * pct) / 100));
  };

  const interpolate = (template, values) =>
    String(template || '').replace(/\{(\w+)\}/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match
    );

  /** Allow-list, no leading or doubled spaces, upper-case for names, then the cap. */
  const clean = (value, allowed, max, upper) => {
    let next = String(value || '')
      .replace(allowed, '')
      .replace(/^\s+/, '')
      .replace(/\s{2,}/g, ' ');
    if (upper) next = next.toUpperCase();
    return Array.from(next).slice(0, max || 12).join('');
  };

  /** Rewrite a field to what will be engraved, keeping the caret in place. */
  const sanitiseField = (input, allowed, max, upper) => {
    if (!input) return;
    const raw = String(input.value || '');
    const next = clean(raw, allowed, max, upper);
    if (next === raw) return;
    const caret = input.selectionStart;
    const removed = raw.length - next.length;
    input.value = next;
    if (caret !== null && input.setSelectionRange) {
      const at = Math.max(0, Math.min(next.length, caret - removed));
      try {
        input.setSelectionRange(at, at);
      } catch (error) {
        /* some input types refuse setSelectionRange */
      }
    }
  };

  const readCart = () =>
    fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

  const changeLine = (key, quantity) =>
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    }).then((r) => r.json());

  const addToCart = (items) =>
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then((response) =>
        response.json().then((data) => {
          if (!response.ok) throw new Error((data && data.description) || 'Cart request failed');
          return data;
        })
      )
      .then((data) => {
        document.dispatchEvent(
          new CustomEvent(CART_UPDATE, { bubbles: true, detail: { data: { source: SOURCE } } })
        );
        return data;
      });

  class CpRcBuybox extends HTMLElement {
    connectedCallback() {
      if (this.dataset.cpRcReady === 'true') return;
      const configEl = qs(this, 'config');
      if (!configEl) return;
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (error) {
        return;
      }
      if (!this.config || !Array.isArray(this.config.variants)) return;

      this.dataset.cpRcReady = 'true';
      this.copy = this.config.copy || {};
      this.keys = this.config.keys || {};
      this.moneyFormat = this.config.moneyFormat || '${{amount}}';

      this.refs = {
        variantId: qs(this, 'variant-id'),
        cta: qs(this, 'cta'),
        error: qs(this, 'error'),
        track: qs(this, 'track'),
        rail: qs(this, 'rail'),
        railPrev: qs(this, 'rail-prev'),
        railNext: qs(this, 'rail-next'),
        lightbox: qs(this, 'lightbox'),
        lightboxImg: qs(this, 'lightbox-img'),
        lightboxHint: qs(this, 'lightbox-hint'),
        nameInput: qs(this, 'name'),
        nameCount: qs(this, 'name-count'),
        phoneInput: qs(this, 'phone'),
        phoneCount: qs(this, 'phone-count'),
        nameOnly: qs(this, 'name-only'),
        propFont: qs(this, 'prop-font'),
        propColour: qs(this, 'prop-colour'),
        propSize: qs(this, 'prop-size'),
        propPack: qs(this, 'prop-pack'),
        extras: qs(this, 'extras'),
        leashToggle: qs(this, 'leash-toggle'),
        leashCollarDot: qs(this, 'leash-collar-dot'),
        leashMatch: qs(this, 'leash-match'),
        leashModal: qs(this, 'leash-modal'),
        sizeModal: qs(this, 'size-modal'),
        configCard: qs(this, 'config-card'),
      };

      this.lists = {
        priceNow: qsa(this, 'price-now'),
        priceWas: qsa(this, 'price-was'),
        priceSave: qsa(this, 'price-save'),
        /* Step 1's head and the total row above the button both show it. */
        summary: qsa(this, 'summary'),
        total: qsa(this, 'total'),
        totalWas: qsa(this, 'total-was'),
        totalSave: qsa(this, 'total-save'),
        ctaLabel: qsa(this, 'cta-label'),
        colourLabel: qsa(this, 'colour-label'),
        colourImg: qsa(this, 'colour-img'),
        plate: qsa(this, 'plate'),
        plateName: qsa(this, 'plate-name'),
        platePhone: qsa(this, 'plate-phone'),
        plateNote: qsa(this, 'plate-note'),
        fontSample: qsa(this, 'font-sample'),
        thumbs: qsa(this, 'thumb'),
        slides: qsa(this, 'slide'),
        progress: qsa(this, 'progress'),
        leashImg: qsa(this, 'leash-img'),
        leashLabel: qsa(this, 'leash-label'),
        leashPicker: qsa(this, 'leash-picker'),
        leashTitle: qsa(this, 'leash-title'),
        leashSub: qsa(this, 'leash-sub'),
        sizeRows: qsa(this, 'size-row'),
        sizePicks: qsa(this, 'size-pick'),
      };

      this.colourInputs = qsa(this, 'colour');
      this.sizeInputs = qsa(this, 'size');
      this.fontInputs = qsa(this, 'font');
      this.tierInputs = qsa(this, 'tier');
      this.leashInputs = qsa(this, 'leash-colour');
      this.extraRows = qsa(this, 'extra-row');

      const start = this.lists.slides.find((el) => el.hasAttribute('data-start'));
      this.state = {
        media: (start || this.lists.slides[0] || { dataset: {} }).dataset.key || 'colour',
        leashOn: false,
        leashPicked: false,
        leashModal: false,
        sizeModal: false,
      };
      this.hasColourSlide = this.lists.slides.some((el) => el.dataset.key === 'colour');
      this.pending = null;

      this.wire();
      this.wireStage();
      this.wireRail();
      this.wireLightbox();
      this.render();
    }

    /**
     * The gallery lightbox: click the stage photo to see it big, click the big
     * one to magnify.
     *
     * The magnified image scales about the point that was clicked, which means
     * the origin has to be worked out from the image's UNSCALED box. Once
     * `transform: scale()` is on, getBoundingClientRect() reports the scaled
     * box, so the rect is captured at the moment of zooming and reused for
     * every later pointer move — reading it live walks the origin away from
     * the pointer.
     *
     * A video slide has no <img>, so it is left alone: tapping a video should
     * play it, not open a still of it.
     */
    wireLightbox() {
      const box = this.refs.lightbox;
      const img = this.refs.lightboxImg;
      if (!box || !img) return;

      let baseRect = null;

      const setHint = (zoomed) => {
        if (!this.refs.lightboxHint) return;
        const text = zoomed ? this.copy.zoomHintOut : this.copy.zoomHint;
        setText(this.refs.lightboxHint, text || '');
        this.refs.lightboxHint.hidden = !text;
      };

      const unzoom = () => {
        baseRect = null;
        box.dataset.zoom = 'false';
        img.style.transformOrigin = '50% 50%';
        setHint(false);
      };

      const originFrom = (event) => {
        const rect = baseRect || img.getBoundingClientRect();
        const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
        const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
        return `${x}% ${y}%`;
      };

      const close = () => {
        box.hidden = true;
        unzoom();
        document.body.classList.remove('cp-rc-locked');
        if (this.lightboxOpener && this.lightboxOpener.focus) {
          this.lightboxOpener.focus({ preventScroll: true });
        }
        this.lightboxOpener = null;
      };
      this.closeLightbox = close;

      this.addEventListener('click', (event) => {
        const slide = event.target.closest('[data-cp-rc="slide"]');
        if (!slide || box.contains(event.target)) return;
        const source = slide.tagName === 'IMG' ? slide : slide.querySelector('img');
        if (!source) return;
        event.preventDefault();
        img.src = source.dataset.cpRcFull || source.currentSrc || source.src;
        img.alt = source.alt || '';
        this.lightboxOpener = source;
        box.hidden = false;
        unzoom();
        document.body.classList.add('cp-rc-locked');
        const button = qs(box, 'lightbox-close');
        if (button) button.focus();
      });

      box.addEventListener('click', (event) => {
        if (event.target === img) {
          if (box.dataset.zoom === 'true') {
            unzoom();
            return;
          }
          baseRect = img.getBoundingClientRect();
          img.style.transformOrigin = originFrom(event);
          box.dataset.zoom = 'true';
          setHint(true);
          return;
        }
        /* The backdrop and the close button both dismiss it. */
        close();
      });

      box.addEventListener('pointermove', (event) => {
        if (box.dataset.zoom !== 'true') return;
        img.style.transformOrigin = originFrom(event);
      });
    }

    disconnectedCallback() {
      if (this.onCartUpdate) document.removeEventListener(CART_UPDATE, this.onCartUpdate);
      if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
      if (this.railObserver) this.railObserver.disconnect();
      if (this.syncRail) window.removeEventListener('resize', this.syncRail);
      document.body.classList.remove('cp-rc-locked');
    }

    /**
     * The thumbnail rail's arrows.
     *
     * They page by whatever is actually visible rather than a fixed number of
     * thumbs, so the same code is right for a wide column and a 360px phone.
     * The whole control hides itself when nothing overflows — a product with
     * three photos should not be offered arrows that do nothing — and each
     * arrow hides at its end of the track rather than sitting there greyed
     * out, because a rail that ends is not an error state.
     */
    wireRail() {
      const rail = this.refs.rail;
      if (!rail) return;

      const page = (direction) => {
        const step = Math.max(rail.clientWidth * 0.8, 120);
        rail.scrollBy({ left: step * direction, behavior: 'smooth' });
      };
      if (this.refs.railPrev) this.refs.railPrev.addEventListener('click', () => page(-1));
      if (this.refs.railNext) this.refs.railNext.addEventListener('click', () => page(1));

      this.syncRail = () => {
        /* Sub-pixel layout means scrollLeft rarely lands exactly on 0 or on
           the maximum, so both ends need a tolerance. */
        const slack = 2;
        const overflows = rail.scrollWidth - rail.clientWidth > slack;
        show(this.refs.railPrev, overflows && rail.scrollLeft > slack);
        show(
          this.refs.railNext,
          overflows && rail.scrollLeft + rail.clientWidth < rail.scrollWidth - slack
        );
      };

      rail.addEventListener('scroll', this.syncRail, { passive: true });
      if (typeof ResizeObserver === 'function') {
        this.railObserver = new ResizeObserver(this.syncRail);
        this.railObserver.observe(rail);
      } else {
        window.addEventListener('resize', this.syncRail);
      }
      this.syncRail();
    }

    /* ------------------------------------------------------------ selection */

    checked(inputs) {
      return inputs.find((input) => input.checked) || null;
    }

    colour() {
      const input = this.checked(this.colourInputs);
      return input ? input.value : (this.config.variants[0] || {}).colour || '';
    }

    size() {
      const input = this.checked(this.sizeInputs);
      return input ? input.value : (this.config.variants[0] || {}).size || '';
    }

    colourMeta() {
      const value = String(this.colour()).toLowerCase();
      const colours = this.config.colours || [];
      return colours.find((c) => String(c.value).toLowerCase() === value) || colours[0] || {};
    }

    sizeLabel() {
      const value = String(this.size()).toLowerCase();
      const meta = (this.config.sizes || []).find((s) => String(s.value).toLowerCase() === value);
      return (meta && meta.label) || this.size();
    }

    fontMeta() {
      const input = this.checked(this.fontInputs);
      const fonts = this.config.fonts || [];
      if (!input) return null;
      return fonts.find((f) => f.id === input.value) || fonts[0] || null;
    }

    tier() {
      const input = this.checked(this.tierInputs);
      const tiers = this.config.tiers || [];
      const found = input ? tiers.find((t) => t.id === input.value) : null;
      return found || { id: 'one', label: '', quantity: 1, percent: 0 };
    }

    variant() {
      const colour = String(this.colour()).toLowerCase();
      const size = String(this.size()).toLowerCase();
      return (
        this.config.variants.find(
          (v) => String(v.colour).toLowerCase() === colour && String(v.size).toLowerCase() === size
        ) || null
      );
    }

    leash() {
      const options = this.config.leash || [];
      const input = this.checked(this.leashInputs);
      return (input && options.find((o) => String(o.id) === input.value)) || options[0] || null;
    }

    /** The lead colour that matches the collar, until the shopper picks one. */
    syncLeashDefault() {
      if (this.state.leashPicked || !this.leashInputs.length) return;
      const meta = this.colourMeta();
      const names = [meta.value, meta.short, meta.label]
        .filter(Boolean)
        .map((n) => String(n).toLowerCase());
      const match = (this.config.leash || []).find(
        (o) => o.available !== false && names.indexOf(String(o.value).toLowerCase()) !== -1
      );
      if (!match) return;
      const input = this.leashInputs.find((i) => i.value === String(match.id));
      if (input && !input.checked && !input.disabled) input.checked = true;
    }

    /* --------------------------------------------------------------- fields */

    nameOnly() {
      return !!(this.refs.nameOnly && this.refs.nameOnly.checked);
    }

    name() {
      if (!this.refs.nameInput) return '';
      return clean(this.refs.nameInput.value, NAME_ALLOWED, this.config.nameMax, true).trim();
    }

    phone() {
      if (!this.refs.phoneInput || this.nameOnly()) return '';
      return clean(this.refs.phoneInput.value, PHONE_ALLOWED, this.config.phoneMax, false).trim();
    }

    isNameField(el) {
      return el === this.refs.nameInput || (el.matches && el.matches('[data-cp-rc="extra-name"]'));
    }

    isPhoneField(el) {
      return el === this.refs.phoneInput || (el.matches && el.matches('[data-cp-rc="extra-phone"]'));
    }

    /* ----------------------------------------------------------------- wire */

    wire() {
      this.addEventListener('input', (event) => {
        const target = event.target;
        if (!target) return;
        if (this.isNameField(target)) {
          sanitiseField(target, NAME_ALLOWED, this.config.nameMax, true);
        } else if (this.isPhoneField(target)) {
          sanitiseField(target, PHONE_ALLOWED, this.config.phoneMax, false);
        } else {
          return;
        }
        this.render();
      });

      this.addEventListener('change', (event) => {
        const target = event.target;
        if (!target) return;
        /* A trailing space is fine mid-typing; once the field is left it is
           trimmed, so the property carries exactly what the plate shows. */
        if ((this.isNameField(target) || this.isPhoneField(target)) && target.value !== target.value.trim()) {
          target.value = target.value.trim();
        }
        if (this.colourInputs.includes(target) && this.hasColourSlide) this.state.media = 'colour';
        if (this.leashInputs.includes(target)) this.state.leashPicked = true;
        if (target === this.refs.leashToggle) this.state.leashOn = target.checked;
        this.render();
      });

      this.addEventListener('click', (event) => {
        const el = event.target;
        const thumb = el.closest('[data-cp-rc="thumb"]');
        if (thumb) {
          event.preventDefault();
          this.state.media = thumb.dataset.key || 'colour';
          this.render();
          return;
        }
        if (el.closest('[data-cp-rc="size-open"]')) {
          event.preventDefault();
          this.opener = el.closest('button');
          this.state.sizeModal = true;
          this.render();
          const close = this.refs.sizeModal && qs(this.refs.sizeModal, 'size-close');
          if (close) close.focus();
          return;
        }
        /* The guide finishes the job it started: pressing a row's button sets
           the picker behind the dialog and closes it, rather than leaving the
           shopper to remember the answer and find the chip again. */
        const pick = el.closest('[data-cp-rc="size-pick"]');
        if (pick) {
          event.preventDefault();
          const wanted = String(pick.dataset.size || '').toLowerCase();
          const input = this.sizeInputs.find((i) => String(i.value).toLowerCase() === wanted);
          if (input && !input.disabled) input.checked = true;
          this.closeModal();
          return;
        }
        if (el.closest('[data-cp-rc="size-close"]') || el === this.refs.sizeModal) {
          event.preventDefault();
          this.closeModal();
          return;
        }
        if (el.closest('[data-cp-rc="leash-open"]')) {
          event.preventDefault();
          this.opener = el.closest('button');
          this.state.leashModal = true;
          this.render();
          const close = this.refs.leashModal && qs(this.refs.leashModal, 'leash-close');
          if (close) close.focus();
          return;
        }
        if (el.closest('[data-cp-rc="leash-close"]') || el === this.refs.leashModal) {
          event.preventDefault();
          this.closeModal();
          return;
        }
        if (el.closest('[data-cp-rc="to-config"]')) {
          event.preventDefault();
          if (this.refs.configCard) {
            this.refs.configCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          return;
        }
        if (el.closest('[data-cp-rc="sticky-cta"]')) {
          event.preventDefault();
          if (this.refs.cta) this.refs.cta.click();
        }
      });

      this.onKeydown = (event) => {
        if (event.key !== 'Escape') return;
        /* The lightbox sits above the other two, so it is dismissed first. */
        if (this.refs.lightbox && !this.refs.lightbox.hidden) {
          if (this.closeLightbox) this.closeLightbox();
          return;
        }
        if (this.state.sizeModal || this.state.leashModal) this.closeModal();
      };
      document.addEventListener('keydown', this.onKeydown);

      /* Blocked, not disabled, while the name is empty: a disabled button
         fires no click, and there would be nothing to hang "here is the field
         you missed" on. Capture phase so Horizon's submit never sees it.
         Enter in a text field submits through this same button. */
      if (this.refs.cta) {
        this.refs.cta.addEventListener(
          'click',
          (event) => {
            if (this.refs.cta.getAttribute('aria-disabled') !== 'true') {
              setText(this.refs.error, '');
              this.pending = { items: this.followLines(), at: Date.now(), name: this.name() };
              return;
            }
            event.preventDefault();
            event.stopImmediatePropagation();
            this.nudgeName();
          },
          true
        );
      }

      /* Horizon's form posts collar 1 and raises cart:update; the rest of the
         order follows it. A stale pending order (the form's add failed and a
         later, unrelated cart change fires) is dropped, never replayed. */
      this.onCartUpdate = (event) => {
        const source = event.detail && event.detail.data && event.detail.data.source;
        if (source === SOURCE) return;
        const pending = this.pending;
        this.pending = null;
        if (!pending || !pending.items.length) return;
        if (Date.now() - pending.at > PENDING_TTL) return;
        this.addRest(pending);
      };
      document.addEventListener(CART_UPDATE, this.onCartUpdate);
    }

    closeModal() {
      this.state.sizeModal = false;
      this.state.leashModal = false;
      this.render();
      if (this.opener && this.opener.focus) this.opener.focus();
    }

    nudgeName() {
      const input = this.refs.nameInput;
      if (!input) return;
      input.setAttribute('aria-invalid', 'true');
      input.focus({ preventScroll: true });
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    /* --------------------------------------------------------------- render */

    render() {
      const variant = this.variant();
      const colour = this.colourMeta();
      const font = this.fontMeta();
      const tier = this.tier();
      const quantity = Math.max(1, Number(tier.quantity) || 1);
      const name = this.name();
      const nameOnly = this.nameOnly();
      const phone = this.phone();
      const leash = this.leash();

      /* Sizes that do not exist in this colour are struck through rather than
         hidden, so the row does not reflow under the finger choosing. */
      this.sizeInputs.forEach((input) => {
        const match = this.config.variants.find(
          (v) =>
            String(v.colour).toLowerCase() === String(this.colour()).toLowerCase() &&
            String(v.size).toLowerCase() === String(input.value).toLowerCase()
        );
        input.disabled = !match || !match.available;
      });

      if (this.refs.variantId && variant) this.refs.variantId.value = variant.id;

      /* ---- money */
      const unit = variant ? Number(variant.price) || 0 : 0;
      const compare = variant ? Number(variant.compareAt) || 0 : 0;
      const leashPrice = this.state.leashOn && leash ? Number(leash.price) || 0 : 0;
      const total = packTotal(unit, quantity, tier.percent) + leashPrice;

      this.lists.priceNow.forEach((node) => setText(node, formatMoney(unit, this.moneyFormat)));
      this.lists.priceWas.forEach((node) => {
        show(node, compare > unit);
        setText(node, formatMoney(compare, this.moneyFormat));
      });
      this.lists.priceSave.forEach((node) => {
        const off = compare > unit ? compare - unit : 0;
        show(node, off > 0);
        setText(
          node,
          interpolate(this.copy.save || 'SAVE {percent}', {
            percent: `${compare > 0 ? Math.round((off / compare) * 100) : 0}%`,
            amount: formatMoney(off, this.moneyFormat),
          })
        );
      });
      this.lists.total.forEach((node) => setText(node, formatMoney(total, this.moneyFormat)));

      /* What the same basket costs undiscounted: the compare-at price where
         there is one, times the pack, plus the lead at its own price so both
         sides of the comparison hold the same things. Hidden when there is
         nothing to compare, so it can never read as a saving of zero. */
      const wasUnit = compare > unit ? compare : unit;
      const wasTotal = wasUnit * quantity + leashPrice;
      const savedTotal = Math.max(0, wasTotal - total);
      this.lists.totalWas.forEach((node) => {
        show(node, savedTotal > 0);
        setText(node, formatMoney(wasTotal, this.moneyFormat));
      });
      this.lists.totalSave.forEach((node) => {
        show(node, savedTotal > 0);
        setText(
          node,
          interpolate(this.copy.save || 'SAVE {percent}', {
            percent: `${wasTotal > 0 ? Math.round((savedTotal / wasTotal) * 100) : 0}%`,
            amount: formatMoney(savedTotal, this.moneyFormat),
          })
        );
      });

      this.tierInputs.forEach((input) => {
        const meta = (this.config.tiers || []).find((t) => t.id === input.value);
        const node = meta && this.querySelector(`[data-cp-rc="tier-price"][data-tier="${meta.id}"]`);
        if (!node) return;
        setText(node, formatMoney(packTotal(unit, meta.quantity, meta.percent), this.moneyFormat));
      });

      /* ---- colour, gallery, labels */
      this.lists.colourImg.forEach((img) => {
        if (colour.image && img.getAttribute('src') !== colour.image) img.src = colour.image;
      });
      this.lists.colourLabel.forEach((node) => setText(node, colour.label || this.colour()));
      const summaryText = `${colour.label || this.colour()} · ${this.sizeLabel()}`;
      this.lists.summary.forEach((node) => setText(node, summaryText));
      this.showSlide(this.state.media);
      /*
       * A video plays (muted, the only way a browser allows autoplay) when its
       * slide comes up, and stops once swiped past. Only on a slide change:
       * render() runs on every keystroke, and replaying there would undo a
       * shopper's own pause.
       */
      if (this.videoKey !== this.state.media) {
        this.videoKey = this.state.media;
        this.lists.slides.forEach((el) => {
          const current = el.dataset.key === this.state.media;
          el.querySelectorAll('video').forEach((video) => {
            if (current) {
              const playing = video.play();
              if (playing && playing.catch) playing.catch(() => {});
            } else if (!video.paused) {
              video.pause();
            }
          });
        });
      }
      this.lists.thumbs.forEach((button) => {
        button.setAttribute('aria-current', String(button.dataset.key === this.state.media));
      });

      /* ---- the plate */
      const example = !name;
      const plateName = example ? this.copy.plateName || '' : name;
      const platePhone = example ? (nameOnly ? '' : this.copy.platePhone || '') : phone;
      this.lists.plate.forEach((node) => {
        node.dataset.example = String(example);
        if (font && font.family) node.style.fontFamily = font.family;
      });
      this.lists.plateName.forEach((node) => setText(node, plateName));
      this.lists.platePhone.forEach((node) => {
        setText(node, platePhone);
        node.hidden = !platePhone;
      });
      this.lists.plateNote.forEach((node) => show(node, example));
      this.lists.fontSample.forEach((node) => setText(node, name || this.copy.fontSample || ''));

      if (this.refs.phoneInput) this.refs.phoneInput.disabled = nameOnly;
      if (this.refs.nameInput && name) this.refs.nameInput.removeAttribute('aria-invalid');
      setText(this.refs.nameCount, `${name.length}/${this.config.nameMax || 10}`);
      setText(
        this.refs.phoneCount,
        nameOnly ? this.copy.phoneNotEngraved || '' : `${phone.length}/${this.config.phoneMax || 12}`
      );

      /* ---- properties, so the packing slip reads back without a lookup */
      if (this.refs.propColour) this.refs.propColour.value = colour.label || this.colour();
      if (this.refs.propSize) this.refs.propSize.value = this.size();
      if (this.refs.propFont && font) this.refs.propFont.value = font.label || '';
      if (this.refs.propPack) {
        this.refs.propPack.value = quantity > 1 ? tier.label || `${quantity}` : '';
        this.refs.propPack.disabled = quantity <= 1;
      }

      /* ---- extra collars in a pack */
      this.extraRows.forEach((row) => {
        const applies = (Number(row.dataset.index) || 0) < quantity - 1;
        row.hidden = !applies;
        row.querySelectorAll('input').forEach((input) => {
          input.disabled = !applies;
        });
        const only = row.querySelector('[data-cp-rc="extra-name-only"]');
        const extraPhone = row.querySelector('[data-cp-rc="extra-phone"]');
        if (applies && only && extraPhone) extraPhone.disabled = only.checked;
      });
      show(this.refs.extras, quantity > 1);

      /* ---- the lead */
      this.syncLeashDefault();
      const chosenLeash = this.leash();
      this.lists.leashPicker.forEach((node) => show(node, this.state.leashOn));
      if (chosenLeash) {
        this.lists.leashImg.forEach((img) => {
          if (chosenLeash.image && img.getAttribute('src') !== chosenLeash.image) img.src = chosenLeash.image;
          img.alt = interpolate(this.copy.leashAlt || '{colour}', { colour: chosenLeash.value });
        });
        this.lists.leashLabel.forEach((node) => setText(node, chosenLeash.value));
      }
      /* The card says what it is now, not what pressing it would do, because
         the checkbox beside it already carries that. The collar chip is there
         because the lead colours are the lead's own and rarely include the
         collar colour — a shopper needs to see they are not the same choice. */
      const leashColour = chosenLeash ? chosenLeash.value : '';
      this.lists.leashTitle.forEach((node) => {
        setText(node, (this.state.leashOn ? this.copy.leashTitleOn : this.copy.leashTitleOff) || '');
      });
      this.lists.leashSub.forEach((node) => {
        const template = (this.state.leashOn ? this.copy.leashSubOn : this.copy.leashSubOff) || '';
        setText(node, interpolate(template, { colour: leashColour }));
      });
      if (this.refs.leashMatch) {
        setText(
          this.refs.leashMatch,
          interpolate(this.copy.leashCollarChip || '', { colour: colour.label || this.colour() })
        );
      }
      if (this.refs.leashCollarDot && colour.dot) this.refs.leashCollarDot.style.background = colour.dot;

      /* ---- the size guide's rows */
      this.lists.sizeRows.forEach((row) => {
        row.dataset.current = String(String(row.dataset.size).toLowerCase() === String(this.size()).toLowerCase());
      });
      this.lists.sizePicks.forEach((button) => {
        const value = String(button.dataset.size || '');
        const current = value.toLowerCase() === String(this.size()).toLowerCase();
        const meta = (this.config.sizes || []).find((s) => String(s.value).toLowerCase() === value.toLowerCase());
        const label = interpolate(
          (current ? this.copy.sizePicked : this.copy.sizePick) || '{size}',
          { size: (meta && meta.label) || value }
        );
        setText(button.querySelector('[data-cp-rc="size-pick-label"]') || button, label);
        button.dataset.current = String(current);
      });

      /* ---- progress: colour & size, then a name, then a number (or name-only) */
      const steps = 1 + (name ? 1 : 0) + (name && (phone || nameOnly) ? 1 : 0);
      this.lists.progress.forEach((node, index) => {
        node.dataset.on = String(index < steps);
      });

      /* ---- the dialogs */
      if (this.refs.sizeModal) this.refs.sizeModal.hidden = !this.state.sizeModal;
      if (this.refs.leashModal) this.refs.leashModal.hidden = !this.state.leashModal;
      document.body.classList.toggle('cp-rc-locked', this.state.sizeModal || this.state.leashModal);

      /* ---- the button */
      const sellable = !!(variant && variant.available);
      const ready = sellable && !!name;
      if (this.refs.cta) {
        this.refs.cta.setAttribute('aria-disabled', String(!ready));
        /* Blocked and sold out both carry aria-disabled, but only one of them
           has a field to send the shopper to. Name the state so the stylesheet
           can keep sold out looking like the dead end it is. */
        this.refs.cta.dataset.state = !sellable ? 'soldout' : ready ? 'ready' : 'blocked';
      }
      const readyCopy = this.config.layout === 'guided' ? this.copy.ctaGuided : this.copy.ctaReady;
      const label = !sellable
        ? this.copy.ctaSoldOut || ''
        : !name
          ? this.copy.ctaBlocked || ''
          : interpolate(readyCopy || '{amount}', { amount: formatMoney(total, this.moneyFormat) });
      this.lists.ctaLabel.forEach((node) => setText(node, label));

      this.painted = true;
    }

    /** Scroll the stage to a slide, guarded against the scroll it causes. */
    showSlide(key) {
      const track = this.refs.track;
      if (!track) return;
      const slide = this.lists.slides.find((el) => el.dataset.key === key);
      if (!slide) return;
      if (Math.abs(track.scrollLeft - slide.offsetLeft) < 2) return;
      this.autoScrolling = true;
      window.clearTimeout(this.autoScrollTimer);
      this.autoScrollTimer = window.setTimeout(() => {
        this.autoScrolling = false;
      }, 600);
      track.scrollTo({ left: slide.offsetLeft, behavior: this.painted ? 'smooth' : 'auto' });
    }

    /** Swiping is the browser's own scrolling; read back where it came to rest. */
    wireStage() {
      const track = this.refs.track;
      if (!track) return;
      let idle;
      track.addEventListener(
        'scroll',
        () => {
          if (this.autoScrolling) return;
          window.clearTimeout(idle);
          idle = window.setTimeout(() => {
            const middle = track.scrollLeft + track.clientWidth / 2;
            let nearest = null;
            let best = Infinity;
            this.lists.slides.forEach((el) => {
              const distance = Math.abs(el.offsetLeft + el.offsetWidth / 2 - middle);
              if (distance < best) {
                best = distance;
                nearest = el;
              }
            });
            if (nearest && nearest.dataset.key !== this.state.media) {
              this.state.media = nearest.dataset.key;
              this.render();
            }
          }, 90);
        },
        { passive: true }
      );
    }

    /* ------------------------------------------------------------- checkout */

    /** Everything that is not collar 1, as /cart/add.js line items. */
    followLines() {
      const items = [];
      const tier = this.tier();
      const quantity = Math.max(1, Number(tier.quantity) || 1);
      const variant = this.variant();
      const font = this.fontMeta();
      const colour = this.colourMeta();
      const firstName = this.name();
      const firstPhone = this.phone();

      if (variant && quantity > 1) {
        this.extraRows.forEach((row) => {
          if ((Number(row.dataset.index) || 0) >= quantity - 1) return;
          const nameEl = row.querySelector('[data-cp-rc="extra-name"]');
          const phoneEl = row.querySelector('[data-cp-rc="extra-phone"]');
          const onlyEl = row.querySelector('[data-cp-rc="extra-name-only"]');
          let extraName = clean(nameEl && nameEl.value, NAME_ALLOWED, this.config.nameMax, true).trim();
          let extraPhone =
            onlyEl && onlyEl.checked
              ? ''
              : clean(phoneEl && phoneEl.value, PHONE_ALLOWED, this.config.phoneMax, false).trim();
          /* A blank extra collar is a spare: same engraving as collar 1. */
          if (!extraName) {
            extraName = firstName;
            extraPhone = firstPhone;
          }
          const properties = {};
          properties[this.keys.name] = extraName;
          if (extraPhone) properties[this.keys.phone] = extraPhone;
          if (font && this.keys.font) properties[this.keys.font] = font.label || '';
          properties[this.keys.colour] = colour.label || this.colour();
          properties[this.keys.size] = this.size();
          properties[this.keys.pack] = tier.label || `${quantity}`;
          items.push({ id: variant.id, quantity: 1, properties });
        });
      }

      const leash = this.leash();
      if (this.state.leashOn && leash && leash.id) items.push({ id: leash.id, quantity: 1 });
      return items;
    }

    /**
     * Add the rest after collar 1 has landed. On failure, collar 1 comes back
     * out — one unit off its own line, found by variant AND engraved name, so
     * a matching collar the shopper already had is never touched.
     */
    addRest(pending) {
      const variant = this.variant();
      readCart()
        .then((cart) => {
          const lines = (cart.items || []).filter(
            (item) =>
              variant &&
              String(item.variant_id) === String(variant.id) &&
              item.properties &&
              item.properties[this.keys.name] === pending.name
          );
          const line = lines[lines.length - 1] || null;
          return addToCart(pending.items).catch((error) => {
            if (!line) throw error;
            return changeLine(line.key, Math.max(0, line.quantity - 1)).then(() => {
              throw error;
            });
          });
        })
        .then(() => setText(this.refs.error, ''))
        .catch(() => {
          setText(this.refs.error, this.copy.addFailed || '');
          document.dispatchEvent(
            new CustomEvent(CART_UPDATE, { bubbles: true, detail: { data: { source: SOURCE } } })
          );
        });
    }
  }

  customElements.define('cp-rc-buybox', CpRcBuybox);

  /**
   * The review cards' "read it in full" dialog, at every width.
   *
   * Each card clamps its body to three lines and the whole card is a button
   * that opens the review at full length, with the photo leading. The dialog
   * shows a *clone* of the card rather than a second copy of the same
   * settings, so the popup and the card can never disagree about what a
   * reviewer said.
   *
   * Below 750px the cards are additionally a snap rail — one in view, the
   * next peeking — but that is the layout's business, not this file's.
   */
  const RV = (name) => `[data-cp-rc-rv="${name}"]`;

  class CpRcRail extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector(RV('modal'));
      this.body = this.querySelector(RV('modal-body'));
      this.opener = null;

      this.addEventListener('click', (event) => {
        const target = event.target;
        if (!target || !target.closest) return;
        const open = target.closest(RV('open'));
        if (open) {
          event.preventDefault();
          this.open(open.closest(RV('card')), open);
          return;
        }
        if (target.closest(RV('close')) || target === this.modal) {
          event.preventDefault();
          this.close();
        }
      });

      this.onKeydown = (event) => {
        if (event.key === 'Escape' && this.modal && !this.modal.hidden) this.close();
      };
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
      document.body.classList.remove('cp-rc-locked');
    }

    open(card, opener) {
      if (!card || !this.modal || !this.body) return;
      const copy = card.cloneNode(true);
      copy.removeAttribute('data-cp-rc-rv');
      /* The block id belongs to the card the theme editor already knows
         about; a second element carrying it would give it two targets. */
      copy.removeAttribute('data-shopify-editor-block');
      copy.classList.add('cp-rc-review--full');
      copy.querySelectorAll('[data-cp-rc-rv], .cp-rc-review__more').forEach((node) => node.remove());
      this.body.textContent = '';
      this.body.appendChild(copy);

      this.opener = opener || null;
      this.modal.hidden = false;
      document.body.classList.add('cp-rc-locked');
      const close = this.modal.querySelector(RV('close'));
      if (close) close.focus();
    }

    close() {
      if (!this.modal) return;
      this.modal.hidden = true;
      document.body.classList.remove('cp-rc-locked');
      if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
      this.opener = null;
    }
  }

  customElements.define('cp-rc-rail', CpRcRail);

  /* "Personalise their collar" buttons in the content sections below. */
  document.addEventListener('click', (event) => {
    const button = event.target.closest && event.target.closest('[data-cp-rc-scroll-buy]');
    if (!button) return;
    const target = document.querySelector('cp-rc-buybox [data-cp-rc="buy"]');
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
})();
